import { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { ClipboardDocumentListIcon, FunnelIcon } from '@heroicons/react/24/outline';
import api from '../../api/client';

// Unified cross-platform activity log — "who did what" across booking + portal.
// Reads GET /api/activity (+ /api/activity/by-actor for the per-staff summary).

const ACTION_LABELS = {
  'checkin.tuition_override': 'Tuition override (check-in)',
  'checkin.edit_in_time': 'Edited check-in time',
  'checkin.edit_out_time': 'Edited check-out time',
  'tuition.late_fee_waive': 'Waived late fee',
  'credit.adjust': 'Adjusted credit/balance',
  'family.void': 'Voided family',
  'attendance.point_edit': 'Edited attendance point',
  'attendance.point_adjust': 'Adjusted attendance points',
  'attendance.event_log': 'Logged attendance event',
  'attendance.event_excuse': 'Excused attendance event',
  'disciplinary.create': 'Issued disciplinary record',
};
const labelFor = (a) => ACTION_LABELS[a] || (a?.startsWith('staff_document.') ? `Staff doc ${a.split('.')[1]}` : a);

function actorName(ev) {
  if (ev.portal_first || ev.portal_last) return `${ev.portal_first || ''} ${ev.portal_last || ''}`.trim();
  return ev.actor_name || ev.actor_email || 'Unknown';
}

function fmtTime(ts) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString('en-US', {
      timeZone: 'America/New_York', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return ts; }
}

function metaSummary(m) {
  if (!m || typeof m !== 'object') return '';
  const parts = [];
  if (m.reason) parts.push(`"${m.reason}"`);
  if (m.amount_due_cents != null) parts.push(`$${(m.amount_due_cents / 100).toFixed(2)} due`);
  if (m.amount_cents != null) parts.push(`$${(m.amount_cents / 100).toFixed(2)}`);
  if (m.waived_cents != null) parts.push(`waived $${(m.waived_cents / 100).toFixed(2)}`);
  if (m.new_check_in_time) parts.push(`→ ${m.new_check_in_time}`);
  if (m.new_check_out_time) parts.push(`→ ${m.new_check_out_time}`);
  if (m.children_voided != null) parts.push(`${m.children_voided} child(ren)`);
  return parts.join(' · ');
}

export default function ActivityLogPage() {
  const [events, setEvents] = useState([]);
  const [byActor, setByActor] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('all');
  const [platform, setPlatform] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (action !== 'all') params.action = action;
      if (platform !== 'all') params.platform = platform;
      if (search.trim()) params.actor_email = search.trim();
      const [list, actors] = await Promise.all([
        api.get('/activity', { params }),
        api.get('/activity/by-actor'),
      ]);
      setEvents(list.data.events || []);
      setByActor(actors.data.actors || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load activity log.');
    } finally { setLoading(false); }
  }, [action, platform, search]);

  useEffect(() => { load(); }, [load]);

  const actionOptions = Object.keys(ACTION_LABELS);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center gap-2">
        <ClipboardDocumentListIcon className="h-7 w-7 text-orange-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Activity Log</h1>
          <p className="text-sm text-gray-500">Who did what across booking + portal — overrides, edits, waives, credits, voids.</p>
        </div>
      </div>

      {/* Per-staff summary */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">By staff member</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-4">Staff</th>
                <th className="py-2 px-2 text-right">Total</th>
                <th className="py-2 px-2 text-right">Overrides</th>
                <th className="py-2 px-2 text-right">Edits</th>
                <th className="py-2 px-2 text-right">Approvals</th>
                <th className="py-2 pl-2">Last action</th>
              </tr>
            </thead>
            <tbody>
              {byActor.length === 0 && (
                <tr><td colSpan={6} className="py-4 text-center text-gray-400">No activity yet.</td></tr>
              )}
              {byActor.map((a) => (
                <tr key={a.actor_email} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="py-2 pr-4 font-medium text-gray-800">{a.actor_name || a.actor_email}</td>
                  <td className="py-2 px-2 text-right">{a.total}</td>
                  <td className="py-2 px-2 text-right">{a.overrides > 0 ? <span className="text-amber-600 font-semibold">{a.overrides}</span> : 0}</td>
                  <td className="py-2 px-2 text-right">{a.edits}</td>
                  <td className="py-2 px-2 text-right">{a.approvals}</td>
                  <td className="py-2 pl-2 text-gray-500">{fmtTime(a.last_action_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <FunnelIcon className="h-5 w-5 text-gray-400" />
        <select value={action} onChange={(e) => setAction(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
          <option value="all">All actions</option>
          {actionOptions.map((a) => <option key={a} value={a}>{labelFor(a)}</option>)}
        </select>
        <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
          <option value="all">Both platforms</option>
          <option value="booking">Booking</option>
          <option value="portal">Portal</option>
        </select>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by staff email…"
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm flex-1 min-w-[180px]"
        />
      </div>

      {/* Event list */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
        ) : events.length === 0 ? (
          <div className="py-12 text-center text-gray-400">No matching activity.</div>
        ) : (
          <div className="divide-y">
            {events.map((ev) => (
              <div key={ev.id} className="px-4 py-3 flex items-start gap-3 hover:bg-gray-50">
                <span className={`mt-1 inline-block w-2 h-2 rounded-full ${ev.platform === 'booking' ? 'bg-blue-400' : 'bg-purple-400'}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900">
                    <span className="font-semibold">{actorName(ev)}</span>
                    {' — '}
                    <span>{labelFor(ev.action)}</span>
                    {ev.target_label && <span className="text-gray-600"> · {ev.target_label}</span>}
                  </div>
                  {metaSummary(ev.metadata) && <div className="text-xs text-gray-500 mt-0.5">{metaSummary(ev.metadata)}</div>}
                </div>
                <div className="text-xs text-gray-400 whitespace-nowrap">{fmtTime(ev.occurred_at)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
